import React, { useEffect, useState } from 'react'
import track from 'react-tracking'

import Accordion from '@ycos/component-accordion'
import Block from '@ycos/component-block'
import { SingleColumn, SectionSpacer } from '@ycos/component-columns'
import { CoremediaRenderer, siteMerchCtaTrack } from '@ycos/coremedia-renderer'
import Divider from '@ycos/component-divider'
import LoyaltyBanner from '@ycos/component-loyalty-banner'
import LoyaltyProductsGrid from '@ycos/component-loyalty-products-grid'
import LoyaltyPersonalShopper from '@ycos/component-loyalty-personal-shopper'
import LoyaltySubText from '@ycos/component-loyalty-subtext'
import RewardsCarousel from '@ycos/component-rewards-carousel'

import BEMHelper from '@ynap/bem-helper'
import LoyaltyCustomerCare from '@ynap/loyalty-customer-care'

import { myaccountPageData } from '../analytics'
import LoyaltyHero from '../LoyaltyHero/LoyaltyHero'
import StickyBottomCollapsible from '../StickyBottomCollapsible/StickyBottomCollapsible'
import YourReservations from '../YourReservations/YourReservations'
import Skeleton from './EIPLandingSkeleton/EIPLandingSkeleton'
import { iscPresets } from './rules'

import type { TierInfo } from '../../utils/loyalty/types'

const bem = new BEMHelper('Rewards')

/*
Container is forced to ltr because otherwise the page layout would be broken on arabic. Each component
of the page has to handle direction independently
*/
const RewardsContainer = ({ children }) => (
    <div className={bem('wrapper')} dir="ltr">
        {children}
    </div>
)
RewardsContainer.layoutVariant = 'isc-collection'

const Rewards = (props) => {
    const {
        userSummary,
        content,
        fetchContent,
        isFetchingContent,
        fetchContentError,
        messages,
        className,
        dir,
        locale,
        config,
        tracking,
    } = props

    const reservationCount = userSummary?.reservationCountTotal || 0
    const { loyaltySegment, loyaltyConsent } = userSummary || {}
    const [tierInfo, setTierInfo] = useState<TierInfo | undefined>(undefined)
    const isRTL = dir === 'rtl'

    useEffect(() => {
        tracking?.trackEvent?.(
            myaccountPageData({
                name: 'rewards',
                subCategory1: 'rewards',
            })(props)
        )
        ;(async () => {
            const data = await window.SF?.getTierInfo(true)
            if (data) setTierInfo(data)
        })()
    }, [])

    useEffect(() => {
        if (!isFetchingContent && !fetchContentError && loyaltySegment && !content) {
            fetchContent(`loyalty-${loyaltySegment}`, null, { mv: 'gitMarkdown' })
        }
    }, [loyaltySegment, fetchContent])

    const components = [
        RewardsContainer,
        SectionSpacer,
        Accordion,
        Block,
        Divider,
        LoyaltyHero,
        SingleColumn,
        LoyaltyBanner,
        LoyaltySubText,
        RewardsCarousel,
        YourReservations,
        LoyaltyCustomerCare,
        LoyaltyProductsGrid,
        LoyaltyPersonalShopper,
        StickyBottomCollapsible,
    ]

    if (!userSummary) {
        return <Skeleton />
    }

    if (loyaltyConsent !== 'true' || (tierInfo && !tierInfo.currentTier)) {
        window.location.assign(config?.head.links?.genericRewardsPage.replace('{locale}', locale.id))
    }

    if (tierInfo && content && loyaltySegment) {
        return (
            <div className={bem(null, null, className)}>
                {CoremediaRenderer(content, components, {
                    rules: [
                        siteMerchCtaTrack,
                        ({ item }) =>
                            iscPresets(item, isRTL, messages, locale, config, loyaltySegment, reservationCount),
                        ({ item }) => {
                            if (item.layoutVariant === 'isc-accordion') {
                                return {
                                    item,
                                    onAccordionOpen: () => {
                                        tracking.trackEvent({
                                            event: 'select_loyalty_rewards_page_faq',
                                        })
                                    },
                                }
                            }
                            if (!tierInfo?.hideLoyaltyMessages && item.layoutVariant === 'isc-sticky-bottom-message') {
                                const [nextTierExpiryContent, exploreNextTierContent, aboutToReachNewTierContent] =
                                    item.items

                                if (tierInfo?.actionStatus === 'EXPIRING')
                                    return {
                                        content: { ...nextTierExpiryContent },
                                        backgroundModifier: tierInfo.currentTier,
                                    }
                                if (tierInfo?.actionStatus === 'ARRIVING')
                                    return {
                                        content: { ...exploreNextTierContent },
                                        backgroundModifier: tierInfo.nextTier,
                                    }
                                if (tierInfo?.actionStatus === 'APPROACHING')
                                    return {
                                        content: { ...aboutToReachNewTierContent },
                                        backgroundModifier: tierInfo.nextTier,
                                    }
                            } else if (item.layoutVariant == 'isc-loyalty-tier-banner') {
                                const {
                                    isStaff,
                                    currentTier,
                                    nextTier,
                                    progress,
                                    lowerThreshold,
                                    upperThreshold,
                                    tierStatus,
                                    actionStatus,
                                    currentSpending,
                                    pendingSpending,
                                    loyaltyConsent,
                                } = tierInfo || {}
                                return {
                                    item,
                                    messages,
                                    currentTier,
                                    nextTier,
                                    loyaltyConsent,
                                    progress: {
                                        value: progress,
                                        intervalMin: lowerThreshold,
                                        intervalMax: upperThreshold,
                                        status: tierStatus,
                                    },
                                    amountSpent: currentSpending,
                                    amountPending: pendingSpending,
                                    isStaff,
                                    isTierExpiring: actionStatus === 'EXPIRING',
                                    colorScheme: item.ynapParameter?.toLowerCase(),
                                }
                            }
                        },
                    ],
                    useDebugRenderingTools: false, // Change to true to debug CM layout variants
                })}
            </div>
        )
    }

    return <Skeleton />
}

export default track({})(Rewards)
